/**
 * ``useRecentDetectionsFeed`` — TanStack Query hook around the global
 * ``GET /api/query/recent-detections`` endpoint (REQ-QUERY-024), seeding
 * the shared :func:`detections/detections-store` buffer. This replaced the
 * Events recent-feed hook under the ADR-032 cascade.
 *
 * This is the authoritative, un-filtered recent-detections source for
 * the Detections panel + (PR5b) the Map glyph layer. Distinct from the
 * analyst+ ``/range/detections`` endpoint (ad-hoc, keyset-paginated) and
 * the per-producer Entity Details detection section. It polls on a short
 * interval so the global window stays fresh; the live SSE merge
 * (:func:`detections/use-detections-sse-bridge`) sharpens the in-view
 * tail to sub-second latency between polls.
 *
 * On each successful fetch the hook upserts the response into the
 * detections store (dedup by ``report_id``; the store prunes by age +
 * caps size). It returns the query result so the panel can render
 * loading / empty / error states.
 *
 * Cross-references:
 *   - REQ-UI-061 (Detections panel).
 *   - REQ-QUERY-024 (recent-detections surface); REQ-QUERY-028 (live SSE).
 *   - REQ-AUTH-024 (operator role; live-map + detections visibility).
 *   - ADR-024 §2 (Detections panel); ADR-032; storyboard Flow 02.
 */
import { useEffect } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";
import { useLiveWindowMs } from "@/chrome/use-deployment-config";
import { type DetectionRecord, useDetectionsStore } from "@/detections/detections-store";

export type RecentDetectionsResponse = components["schemas"]["RecentDetectionsResponse"];
type DetectionItem = components["schemas"]["DetectionItem"];

/**
 * Read roles that may see the live map + its detections (mirrors the BFF
 * ``map_reader`` gate — same set as the recent-events feed).
 */
export const RECENT_DETECTIONS_FEED_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/** Poll cadence keeping the global window fresh between live SSE deltas. */
export const RECENT_DETECTIONS_FEED_REFETCH_MS = 10_000;

/** Result cap requested from the BFF (its hard max is 100). */
export const DETECTION_FEED_LIMIT = 100;

export const RECENT_DETECTIONS_FEED_QUERY_KEY = ["recent-detections-feed"] as const;

/** Map the wire :type:`DetectionItem` to the store's :type:`DetectionRecord`. */
export function detectionRecordFromApi(item: DetectionItem): DetectionRecord {
  return {
    report_id: item.report_id,
    object_id: item.object_id,
    track_id: item.track_id,
    producer_id: item.producer_id,
    time: item.time,
    latitude_deg: item.latitude_deg,
    longitude_deg: item.longitude_deg,
    elevation_m: item.elevation_m,
    state: item.state,
    task_id: item.task_id,
    detection_confidence: item.detection_confidence,
    primary_class: item.primary_class,
    schema_version: item.schema_version,
    source_aggregator_id: item.source_aggregator_id,
    ingress_timestamp: item.ingress_timestamp,
    message_hash: item.message_hash,
    source_network: item.source_network ?? null,
  };
}

/**
 * Poll the global recent-detections endpoint and seed the detections
 * store.
 *
 * Safe to mount from multiple surfaces (the Detections panel + (PR5b) the
 * Map glyph layer): TanStack Query dedups concurrent observers of the
 * shared query key onto a single in-flight request + cache entry.
 */
export function useRecentDetectionsFeed(): UseQueryResult<RecentDetectionsResponse, ApiError> {
  const session = useSession();
  const enabled = session.hasAnyRole(RECENT_DETECTIONS_FEED_READER_ROLES);
  // The deployment live window (ADR-041 D-7; REQ-UI-181) — the buffer's prune
  // floor, so the panel and the map agree with the BFF on "recent".
  const retentionMs = useLiveWindowMs();
  const query = useQuery<RecentDetectionsResponse, ApiError>({
    queryKey: RECENT_DETECTIONS_FEED_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(
        `/api/query/recent-detections?limit=${DETECTION_FEED_LIMIT}`,
        { signal },
      );
      if (!isResponseShape(raw)) {
        console.error(
          "[meridian/detections/use-recent-detections-feed] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "recent-detections response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    refetchInterval: RECENT_DETECTIONS_FEED_REFETCH_MS,
    staleTime: 0,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Seed the shared buffer on each successful fetch. Keyed on the data
  // reference so it runs once per distinct response (TanStack returns a
  // stable reference for an unchanged cache entry).
  const data = query.data;
  useEffect(() => {
    if (data !== undefined) {
      useDetectionsStore.getState().upsert(data.items.map(detectionRecordFromApi), {
        retentionMs,
      });
    }
  }, [data, retentionMs]);

  return query;
}

function isResponseShape(value: unknown): value is RecentDetectionsResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.items) &&
    typeof obj.truncated === "boolean" &&
    typeof obj.duration_ms === "number"
  );
}
